import { PRODUCTION_IPS } from "@syla/shared/helpers/ipAddresses";
import { assertNever } from "@syla/shared/types/helpers/errors";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import {
  SyncWalletErrorDetails,
  SyncWalletLimitType,
  SyncWalletAuthErrorReason,
  SyncWalletErrorDetailCode,
  SyncWalletAuthErrorDetails,
} from "@syla/shared/types/models/WalletBase";
import { formatDuration, intervalToDuration } from "date-fns";
import { UnexpectedErrorString } from "../../../components/atoms/UnexpectedError";

export function parseSyncErrorMessage(
  syncAuthType: "oauth" | undefined,
  details: SyncWalletErrorDetails,
  dataSourceType: DataSourceType,
): {
  title: string;
  message: string;
} {
  const code = details.code;

  if (code === SyncWalletErrorDetailCode.LIMIT_EXCEEDED) {
    const title = "Transaction limit reached";

    if (details.limitType === SyncWalletLimitType.DATA_SOURCE) {
      let message =
        "This data source has more transactions than Syla can import.";

      if (
        [DataSourceType.Blockchain, DataSourceType.Wallet].includes(
          dataSourceType,
        )
      ) {
        message += ` Please check that you've entered the correct address.`;
      }

      return {
        title,
        message,
      };
    }

    return {
      title,
      message: `This data source can not be imported as it would exceed the total transaction limit allowed.`,
    };
  }

  if (code === SyncWalletErrorDetailCode.INVALID_ADDRESS) {
    return {
      title: "Invalid address",
      message:
        "The address you entered isn’t valid. Please double-check the address and try again.",
    };
  }

  if (code === SyncWalletErrorDetailCode.INVALID_AUTH) {
    return parseInvalidAuth(syncAuthType, details);
  }

  if (code === SyncWalletErrorDetailCode.RATE_LIMIT) {
    return parseRateLimitErrorMessage(
      details.vendor,
      details.createdMs,
      details.retryAfterMs,
    );
  }

  if (code === SyncWalletErrorDetailCode.OUTAGE) {
    return {
      title: "Temporary outage",
      message: `We're aware of an issue affecting this integration and we are working to resolve it. Please try again in a few hours.`,
    };
  }

  if (code === SyncWalletErrorDetailCode.CUSTOM) {
    return {
      title: details.title,
      message: details.message,
    };
  }

  if (code === SyncWalletErrorDetailCode.UNKNOWN) {
    return {
      title: "Unknown sync error",
      message: UnexpectedErrorString,
    };
  }

  assertNever(code);
}

function parseInvalidAuth(
  syncAuthType: "oauth" | undefined,
  details: SyncWalletAuthErrorDetails,
): { title: string; message: string } {
  const isOauth = syncAuthType === "oauth";

  const reason = details.subReason;

  if (reason === SyncWalletAuthErrorReason.InvalidKey) {
    if (isOauth) {
      return {
        title: "Connection failed",
        message:
          "Syla couldn't connect to the exchange. Please try connecting again.",
      };
    }

    return {
      title: "Invalid API key",
      message:
        "Syla couldn't connect to the exchange using your API key. Please check the API key was entered correctly, or create a new API key and try again.",
    };
  }

  if (reason === SyncWalletAuthErrorReason.InsufficientPermission) {
    const title = "Couldn’t access transaction history";

    if (isOauth) {
      return {
        title,
        message:
          "Syla connected to your exchange, but doesn't have permission to view your transaction history. Please check you enabled read-only access to your full transaction history, then try again.",
      };
    }
    return {
      title,
      message:
        "Syla connected to your exchange, but doesn't have permission to view your transaction history. Please update your API key so it has read-only access to your full transaction history, then try again.",
    };
  }

  if (reason === SyncWalletAuthErrorReason.ExcessivePermission) {
    if (isOauth) {
      return {
        title: "Too much access",
        message:
          "Your connection was rejected because it allows trading or fund transfers. Please reconnect this data source with read-only permissions.",
      };
    }

    return {
      title: "API key provides too much access",
      message:
        "Your API key was rejected because it allows trading or fund transfers. For your security, Syla only accepts read-only keys. Please delete this key on your exchange and create a new read-only API key, then try again.",
    };
  }

  if (reason === SyncWalletAuthErrorReason.Expired) {
    if (isOauth) {
      return {
        title: "Connection has expired",
        message:
          "Your connection has expired. Please reconnect this data source.",
      };
    }

    return {
      title: "API key has expired",
      message:
        "Your API key has expired and can no longer be used. Please create a new API key and try again.",
    };
  }

  if (reason === SyncWalletAuthErrorReason.InvalidIpRestriction) {
    const title = "Incorrect IP restriction";

    if (isOauth) {
      return {
        title,
        message: `IP restrictions are enabled, but the allowed IPs are incorrect. Please update the connection to allow the following IPs: ${PRODUCTION_IPS.join(",")}`,
      };
    }

    return {
      title,
      message: `Your API key has IP restrictions enabled, but the allowed IPs are incorrect. Please update the API key to allow the following IPs: ${PRODUCTION_IPS.join(",")}`,
    };
  }

  if (reason === SyncWalletAuthErrorReason.ValidKeyNotUsable) {
    return {
      title: "API key can't be used",
      message: `Your API key was recognised but can't be used by Syla. Please create a new read-only API key just for Syla, then try again.`,
    };
  }

  assertNever(reason);
}

function parseRateLimitErrorMessage(
  vendor: string | undefined,
  createdMs: number,
  timespanMs: number | undefined,
): { title: string; message: string } {
  // time may have already elapsed since error was created
  const waitRemaining =
    timespanMs ? timespanMs - (Date.now() - createdMs) : undefined;

  const tryAgainMessage =
    waitRemaining === undefined ? `Please try again in a few hours`
    : waitRemaining > 0 ?
      `Please try again in ${formatDuration(
        intervalToDuration({ start: 0, end: waitRemaining }),
      )}`
    : `You can try it again now`;

  return {
    title: "Connection temporarily paused",
    message: `${
      vendor ?
        `${vendor.trim()} paused your connection`
      : `The connection was paused`
    } for syncing too often. 
    ${tryAgainMessage}.`,
  };
}
